import {
    DSFormControl,
    DSFormControlLabel,
    DSFormLabel,
    DSRadio,
    DSRadioGroup,
    Loading,
} from '@deltasierra/components';
import { filterUnique } from '@deltasierra/utilities-array';
import { MAX_FACEBOOK_HASHTAGS_COUNT } from '@deltasierra/shared';
import { FormikProps } from 'formik';
import * as React from 'react';
import styled from 'styled-components';
import { Translate } from '../../../../directives/Translate';
import { HashtagsInputWithHashtagSetsDropdown, HashtagsLimitHelpBlock } from '../../components';
import { PublishHelpBlock } from '../../components/PublishHelpBlock';
import { FacebookFormValues, HashtagInsertType } from '../facebook-form-values';

const StyledRadioGroup = styled(DSRadioGroup)`
    margin-bottom: 0;
`;

export type FacebookFirstCommentHashtagsProps = {
    availableCharacterLength: number;
    formik: FormikProps<FacebookFormValues>;
    locationId: string;
    locationIds: string[];
    requiredHashtags: string[];
    requiredHashtagsLoading: boolean;
};

export function FacebookFormHashtagsSectionInner({
    availableCharacterLength,
    formik,
    locationId,
    locationIds,
    requiredHashtags,
    requiredHashtagsLoading,
}: FacebookFirstCommentHashtagsProps): JSX.Element {
    const handleChange: React.Dispatch<React.SetStateAction<string[]>> = React.useCallback(
        newValue => {
            formik.setValues(prevState => ({
                ...prevState,
                hashtags: typeof newValue === 'function' ? newValue(prevState.hashtags) : newValue,
            }));
        },
        [formik],
    );

    // Anytime the required hashtags are updated, we append them to the start of the hashtag list
    React.useEffect(() => {
        formik.setValues(prevState => ({
            ...prevState,
            hashtags: [...requiredHashtags, ...prevState.hashtags].filter(filterUnique()),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requiredHashtags]);

    const totalHashtagsPossibleLength = MAX_FACEBOOK_HASHTAGS_COUNT * ' '.length;

    return (
        <>
            {requiredHashtagsLoading && <Loading inline size="small" />}
            <HashtagsInputWithHashtagSetsDropdown
                disabledHashtags={requiredHashtags}
                error={!!formik.errors.hashtags}
                gutterBottom
                helperText={
                    <>
                        <HashtagsLimitHelpBlock
                            hashtags={formik.values.hashtags}
                            maxCharacters={availableCharacterLength}
                            maxTags={MAX_FACEBOOK_HASHTAGS_COUNT}
                        />
                        {formik.errors.hashtags && <div>{formik.errors.hashtags}</div>}
                    </>
                }
                locationId={locationId}
                maxHashtags={MAX_FACEBOOK_HASHTAGS_COUNT}
                maxLength={availableCharacterLength - totalHashtagsPossibleLength}
                value={formik.values.hashtags}
                onChange={handleChange}
            />
            <DSFormControl>
                <DSFormLabel>
                    <Translate keyId="BUILD.PUBLISH.FACEBOOK.INPUT_LABELS.DISPLAY_HASHTAGS_IN" />
                </DSFormLabel>
                <StyledRadioGroup
                    value={formik.values.hashtagsInsertType}
                    onChange={event =>
                        formik.setFieldValue('hashtagsInsertType', event.target.value as HashtagInsertType)
                    }
                >
                    <DSFormControlLabel
                        control={<DSRadio />}
                        label={
                            <Translate keyId="BUILD.PUBLISH.FACEBOOK.INPUT_LABELS.DISPLAY_HASHTAGS_IN_FIRST_COMMENT" />
                        }
                        value="comment"
                    />
                    <DSFormControlLabel
                        control={<DSRadio />}
                        label={<Translate keyId="BUILD.PUBLISH.FACEBOOK.INPUT_LABELS.DISPLAY_HASHTAGS_IN_CAPTION" />}
                        value="caption"
                    />
                </StyledRadioGroup>
                <PublishHelpBlock emphasis>
                    {formik.values.hashtagsInsertType === 'caption' && (
                        <Translate keyId="BUILD.PUBLISH.FACEBOOK.HASHTAGS_HELP_TEXT_CAPTION" />
                    )}
                    {formik.values.hashtagsInsertType === 'comment' && (
                        <Translate keyId="BUILD.PUBLISH.FACEBOOK.HASHTAGS_HELP_TEXT_FIRST_COMMENT" />
                    )}
                </PublishHelpBlock>
            </DSFormControl>
        </>
    );
}
FacebookFormHashtagsSectionInner.displayName = 'FacebookFormHashtagsSectionInner';
